<template>
  <WdsStack direction-column gap-lg>
    <div v-if="jobLoadingError" class="tw-mt-9">
      <ContentLoadingErrorNotice />
    </div>

    <template v-else>
      <div v-if="!loading && job">
        <div v-if="!supplierViewer" class="tw-mb-8">
          <TalentChannelsSummary
            :company-id="companyId"
            :job-id="jobId"
            :is-published="jobObj?.published"
            :is-available="jobObj?.available"
            :is-for-marketplace="jobObj?.isForMarketplace"
            :count-staffing-agency-shares="jobObj?.recruiterShares?.paginatorInfo?.total ?? 0"
            :staffing-agency-share-names="staffingAgencyShareNames"
            :viewer-can-share="jobObj?.viewerCanShare ?? false"
            :has-company-suppliers="hasCompanySuppliers"
            :contact-invites-url="contactInvitesUrl"
            :single-supplier-id="singleSupplierId"
            :single-supplier-name="singleSupplierName"
            :single-supplier-share-id="singleSupplierShareId"
            :contact-name="jobObj?.contactName"
            :contact-email="jobObj?.contactEmail"
            :contact-phone="jobObj?.contactPhone"
            @contacts-updated="handleContactsUpdated"
          />
        </div>
        <CandidatesSection
          :job="job"
          :has-single-supplier="hasSingleSupplier"
          :supplier-viewer="supplierViewer"
          :viewer-account-id="viewerAccountId"
          :rate-cards-enabled="rateCardsEnabled"
        />
      </div>

      <div v-else class="tw-flex tw-mt-6 tw-justify-center">
        <WdsSpinner :size="40" indeterminate />
      </div>
    </template>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { WdsSpinner, WdsStack } from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'

import { useFragment } from '@/gql'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import SilentNetworkError from '@/utils/notification/SilentNetworkError'
import ContentLoadingErrorNotice from '@/components/ContentLoadingErrorNotice.vue'

import { GetJobQuery } from '@/components/Job/graphql/queries'
import { JobFragment } from '@/components/Job/graphql/fragments'

import CandidatesSection from '@/components/Job/Candidates/CandidatesSection.vue'
import TalentChannelsSummary from '@/components/Job/Candidates/TalentChannelsSummary.vue'

const props = defineProps<{
  companyId: string
  jobId: string
  hasCompanySuppliers?: boolean
  contactInvitesUrl?: string
  singleSupplierId?: string
  singleSupplierName?: string
  supplierViewer?: boolean
  viewerAccountId?: string
  rateCardsEnabled?: boolean
}>()

const {
  result: jobResult,
  loading,
  refetch: refetchJob,
  onError: onJobLoadingError,
} = useQuery(
  GetJobQuery,
  {
    jobId: props.jobId,
  },
  { clientId: 'publicApi' },
)

const jobLoadingError = ref(false)
const jobObj = ref(useFragment(JobFragment, null))

const job = computed(() => jobResult.value?.job)

watch(
  () => job.value,
  () => {
    jobObj.value = useFragment(JobFragment, job.value) ?? null
  },
)

const getJob = () =>
  publicApi.readFragment({
    id: `Job:${props.jobId}`,
    fragment: JobFragment,
  })

const staffingAgencyShareNames = computed(() =>
  (jobObj.value?.recruiterShares?.data ?? [])
    .map((share: any) => share.account?.name)
    .filter(Boolean),
)

const singleSupplierShareId = computed(() => {
  if (!props.singleSupplierId) return undefined
  const share = (jobObj.value?.recruiterShares?.data ?? []).find(
    (s: any) => s.account?.id === props.singleSupplierId,
  )
  return share?.id
})

const hasSingleSupplier = computed(() => !!props.singleSupplierId && !!props.singleSupplierName)

const handleContactsUpdated = async () => {
  await refetchJob()
  jobObj.value = useFragment(JobFragment, jobResult.value?.job) ?? null
}

onJobLoadingError((error) => {
  jobLoadingError.value = true

  new SilentNetworkError({
    ex: error,
  }).dispatch()
})
</script>
